import {
  type CreateTourScreenshotFrameArgs,
  selectOrganizationId,
  useCreateTourScreenshotFrameMutation,
  useUploadToursScreenshotMutation,
} from '@carboncopyco/data';
import { getInitialStep } from '@carboncopyco/mimesis-shared';
import {
  FrameEventType,
  FrameType,
  RelativePositionTypeEnums,
  type TourFrame,
} from '@carboncopyco/types';
import { notify } from '@carboncopyco/ui';
import { base64toFile } from '@carboncopyco/utils';
import { v4 as generateUUID } from 'uuid';
import { useTourEditorPageProps } from '../pages/TourEditor/TourEditorPageContext';
import { preloadImage } from '../utils/preloadImage';
import { getStepConfig } from '../utils/tooltip';
import { useTourSelector } from './store';

type UseProcessImageAsFrameReturnType = {
  createImageFrame: (data: CreateImageFrameType) => Promise<void>;
  uploadAnImage: (
    img: string,
    newTourId: number,
    fileServiceToken: string
  ) => Promise<string | null>;
};

type CreateImageFrameType = {
  img: string;
  selectedFrameIndex: number;
  tourId: number;
  playableFrames: TourFrame[];
  activeFrame: TourFrame;
  showTooltip: boolean;
  fileServiceToken: string;
};

type UseProcessImageAsFrameProps = {
  fileServiceDynamicLink: string;
};

export const useProcessImageAsFrame = ({
  fileServiceDynamicLink,
}: UseProcessImageAsFrameProps): UseProcessImageAsFrameReturnType => {
  const organizationId = useTourSelector(selectOrganizationId);
  const { rootElement } = useTourEditorPageProps();

  const [createTourScreenshotFrame] = useCreateTourScreenshotFrameMutation();
  const [uploadToursScreenshot] = useUploadToursScreenshotMutation();

  const createImageFrame = async ({
    img,
    selectedFrameIndex,
    tourId,
    playableFrames,
    activeFrame,
    showTooltip,
    fileServiceToken,
  }: CreateImageFrameType): Promise<void> => {
    const imgUrl = await uploadAnImage(img, tourId, fileServiceToken);
    if (!imgUrl) {
      return Promise.reject();
    }

    return createScreenshotFrame({
      img: imgUrl,
      selectedFrameIndex,
      tourId,
      playableFrames,
      activeFrame,
      showTooltip,
      fileServiceToken,
    });
  };

  const uploadAnImage = async (
    img: string,
    tourId: number,
    fileServiceToken: string
  ): Promise<string | null> => {
    if (!img) {
      return null;
    }

    const file = base64toFile(img);
    const arrayBuffer = await file.arrayBuffer();

    const tourResponse = await uploadToursScreenshot({
      baseUrl: fileServiceDynamicLink,
      organizationId,
      tourId: tourId,
      screenshotId: `screenshot-${generateUUID()}`,
      token: fileServiceToken,
      fileData: {
        arrayBuffer,
      },
    }).unwrap();

    if (tourResponse) {
      // preload image
      const path = tourResponse.path_to_image;
      preloadImage(`${path}?token${fileServiceToken}`);

      return path;
    }

    notify.error('Something went wrong.');

    return null;
  };

  const createScreenshotFrame = async ({
    img: imgUrl,
    selectedFrameIndex,
    tourId,
    playableFrames,
    activeFrame,
    showTooltip,
  }: CreateImageFrameType): Promise<any> => {
    // Generate default step
    const step = {
      ...getInitialStep(false, '', FrameType.screenshot, {
        relation: RelativePositionTypeEnums.page,
        alignment: 'middleCenter',
      }),
      title: 'Untitled step',
      stepId: generateUUID(),
      showTooltip: showTooltip,
    };

    // Pick step config from frame
    const stepConfig = getStepConfig({
      step,
      activeFrame: activeFrame,
      playableFrames,
      frameType: FrameType.screenshot,
      shouldInheritConfig: true,
      rootElement,
    });

    const stepWithConfig = { ...step, config: stepConfig };

    const data: CreateTourScreenshotFrameArgs = {
      organizationId,
      frameActionLabel: 'Untitled Frame',
      frameIndex: selectedFrameIndex,
      frameTitle: 'Untitled Frame',
      pageUrl: '',
      tourId,
      pageTitle: '',
      frameEventType: FrameEventType.finish,
      frameScreenshotUrl: imgUrl,
      frameType: FrameType.screenshot,
      steps: [stepWithConfig],
    };

    return createTourScreenshotFrame(data).unwrap();
  };

  return {
    createImageFrame,
    uploadAnImage,
  };
};
